import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { EmptyValue } from '@/components/common/EmptyValue';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ACCENT_TONE_VAR_CLASS, type AccentTone } from '@/styles/accentTone';
import { cn } from '@/lib/utils';
import { toCapsLabel } from '@/utils/capsLabel';
import { formatNumber } from '@/utils/number';
import { getStatValueText } from './statCardWidths';

export type StatCardDensity = 'regular' | 'compact';

interface StatCardSizeClass {
  card: string;
  value: string;
  valueLine: string;
  valueSkeleton: string;
  label: string;
  labelLine: string;
}

const SCREEN_WIDTH_SIZE_CLASS: StatCardSizeClass = {
  card: 'gap-1 px-4 py-2.5',
  value: 'text-xl sm:text-2xl',
  valueLine: 'h-7 sm:h-8',
  valueSkeleton: 'h-6 sm:h-7',
  label: 'truncate text-3xs sm:text-2xs',
  labelLine: 'h-4 leading-4',
};

const COMPACT_SIZE_CLASS: StatCardSizeClass = {
  card: 'gap-1 px-3 py-2',
  value: 'text-xl sm:text-2xl',
  valueLine: 'h-7 sm:h-8',
  valueSkeleton: 'h-6 sm:h-7',
  label: 'text-3xs sm:text-2xs',
  labelLine: 'min-h-4 leading-4',
};

const SIZE_CLASS_BY_DENSITY: Record<StatCardDensity, StatCardSizeClass> = {
  regular: SCREEN_WIDTH_SIZE_CLASS,
  compact: COMPACT_SIZE_CLASS,
};

const FEATURE_SIZE_CLASS: StatCardSizeClass = {
  card: 'gap-1 px-5 py-5',
  value: 'text-3xl',
  valueLine: 'h-9',
  valueSkeleton: 'h-8',
  label: 'truncate text-2xs',
  labelLine: 'h-4 leading-4',
};

const CARD_SURFACE_CLASS = 'rounded-lg shadow-panel ring-1 ring-border';
const VALUE_ROW_CLASS = 'mt-auto flex items-center justify-between gap-3';
const FEATURE_ICON_CIRCLE_CLASS = cn(
  'flex size-12 shrink-0 items-center justify-center rounded-full',
  'bg-[color-mix(in_oklab,var(--accent-tone)_18%,transparent)] text-(--accent-tone)',
);
const FEATURE_ICON_CLASS = 'size-6';
const FEATURE_VALUE_ADDON_LINE_CLASS = 'min-h-4 text-xs text-muted-foreground';
const LABEL_CLASS = 'font-medium tracking-wider text-muted-foreground';
const VALUE_SKELETON_CLASS = 'w-16';
const LABEL_SKELETON_CLASS = 'h-2.5 w-20 max-w-full';
const LINE_BOX_CLASS = 'flex items-center';

interface StatValueLineProps {
  value: number | string | undefined;
  unit?: string | undefined;
  isError?: boolean | undefined;
  className: string;
}

function StatValueLine({ value, unit, isError, className }: StatValueLineProps) {
  const exactValueText = typeof value === 'number' ? formatNumber(value) : undefined;

  return (
    <span
      className={cn(
        'flex min-w-0 flex-wrap items-baseline gap-x-1 leading-tight font-medium tracking-tight text-foreground tabular-nums',
        className,
      )}
      aria-live="polite"
      title={isError ? 'Failed to load' : exactValueText}
    >
      <span className="whitespace-nowrap">
        {isError ? <EmptyValue /> : getStatValueText(value)}
      </span>
      {!isError && unit && (
        <span className="text-xs font-medium whitespace-nowrap text-muted-foreground">{unit}</span>
      )}
    </span>
  );
}

interface StatCardContent {
  label: string;
  value: number | string | undefined;
  unit?: string | undefined;
  hint?: string | undefined;
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
}

interface ValueSkeletonLineProps {
  sizeClass: StatCardSizeClass;
}

function ValueSkeletonLine({ sizeClass }: ValueSkeletonLineProps) {
  return (
    <span className={cn(LINE_BOX_CLASS, sizeClass.valueLine)} aria-hidden>
      <Skeleton className={cn(VALUE_SKELETON_CLASS, sizeClass.valueSkeleton)} />
    </span>
  );
}

interface StatCardFrameProps extends StatCardContent {
  sizeClass: StatCardSizeClass;
  valueTrailing?: ReactNode;
  footer?: ReactNode;
  className?: string | undefined;
}

function StatCardFrame({
  label,
  value,
  unit,
  hint,
  isLoading,
  isError,
  sizeClass,
  valueTrailing,
  footer,
  className,
}: StatCardFrameProps) {
  return (
    <Card className={cn(CARD_SURFACE_CLASS, sizeClass.card, className)}>
      <span className={cn(LABEL_CLASS, sizeClass.label, sizeClass.labelLine)} title={hint ?? label}>
        {toCapsLabel(label)}
      </span>
      <div className={VALUE_ROW_CLASS}>
        {isLoading ? (
          <ValueSkeletonLine sizeClass={sizeClass} />
        ) : (
          <StatValueLine value={value} unit={unit} isError={isError} className={sizeClass.value} />
        )}
        {valueTrailing}
      </div>
      {footer}
    </Card>
  );
}

interface StatCardSkeletonProps {
  density: StatCardDensity;
  className?: string | undefined;
}

export function StatCardSkeleton({ density, className }: StatCardSkeletonProps) {
  const sizeClass = SIZE_CLASS_BY_DENSITY[density];

  return (
    <Card className={cn(CARD_SURFACE_CLASS, sizeClass.card, className)} aria-hidden>
      <span className={cn(LINE_BOX_CLASS, sizeClass.labelLine)}>
        <Skeleton className={LABEL_SKELETON_CLASS} />
      </span>
      <div className={VALUE_ROW_CLASS}>
        <ValueSkeletonLine sizeClass={sizeClass} />
      </div>
    </Card>
  );
}

interface StatCardProps extends StatCardContent {
  density: StatCardDensity;
  className?: string | undefined;
}

export function StatCard({ density, ...cardProps }: StatCardProps) {
  return <StatCardFrame {...cardProps} sizeClass={SIZE_CLASS_BY_DENSITY[density]} />;
}

interface FeatureStatCardProps extends StatCardContent {
  icon: LucideIcon;
  accent: AccentTone;
  valueAddon?: ReactNode;
  className?: string | undefined;
}

export function FeatureStatCard({
  icon: Icon,
  accent,
  valueAddon,
  className,
  ...cardProps
}: FeatureStatCardProps) {
  const showsValueAddon = !cardProps.isLoading && !cardProps.isError && valueAddon !== undefined;

  return (
    <StatCardFrame
      {...cardProps}
      sizeClass={FEATURE_SIZE_CLASS}
      className={cn(ACCENT_TONE_VAR_CLASS[accent], className)}
      valueTrailing={
        <span className={FEATURE_ICON_CIRCLE_CLASS} aria-hidden>
          <Icon className={FEATURE_ICON_CLASS} />
        </span>
      }
      footer={
        <div className={FEATURE_VALUE_ADDON_LINE_CLASS}>{showsValueAddon ? valueAddon : null}</div>
      }
    />
  );
}
